<template>
  <div
    ref="resizerRef"
    class="column-resizer"
    :class="{ resizing: isResizing }"
    @mousedown="startResize"
  >
    <div class="resizer-handle"></div>
  </div>
</template>

<script setup>
  import { ref, onUnmounted } from 'vue'

  const props = defineProps({
    columnIndex: {
      type: Number,
      required: true
    },
    minWidth: {
      type: Number,
      default: 50
    },
    maxWidth: {
      type: Number,
      default: 500
    }
  })

  const emit = defineEmits(['resize'])

  const resizerRef = ref(null)
  const isResizing = ref(false)
  const startX = ref(0)
  const startWidth = ref(0)

  const startResize = event => {
    event.preventDefault()
    event.stopPropagation()

    isResizing.value = true
    startX.value = event.clientX

    const table = resizerRef.value.closest('table')
    const headerCell = table.querySelector(
      `thead th:nth-child(${props.columnIndex + 1})`
    )

    const computedStyle = window.getComputedStyle(headerCell)
    startWidth.value = parseFloat(computedStyle.width) || headerCell.offsetWidth

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const handleMouseMove = event => {
    if (!isResizing.value) return

    const deltaX = event.clientX - startX.value
    const newWidth = Math.max(
      props.minWidth,
      Math.min(props.maxWidth, startWidth.value + deltaX)
    )

    emit('resize', {
      columnIndex: props.columnIndex,
      width: newWidth
    })
  }

  const stopResize = () => {
    isResizing.value = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', stopResize)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', stopResize)
  })
</script>

<style scoped>
  .column-resizer {
    position: absolute;
    top: 0;
    right: -3px;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .column-resizer:hover .resizer-handle,
  .column-resizer.resizing .resizer-handle {
    background-color: #3b82f6;
    width: 2px;
  }

  .resizer-handle {
    width: 1px;
    height: 100%;
    background-color: #d1d5db;
    transition: all 0.2s ease;
  }

  .column-resizer:hover,
  .column-resizer.resizing {
    background-color: rgba(59, 130, 246, 0.1);
  }

  .resizing {
    background-color: rgba(59, 130, 246, 0.2);
  }
</style>
